import { createRoutine } from 'redux-saga-routines'
import {
  call,
  delay,
  fork,
  put,
  select,
  take,
  spawn,
  takeLatest
} from '@redux-saga/core/effects'
import { eventChannel } from 'redux-saga'
import { head, pluck } from 'ramda'
import { isFalsy } from 'ramda-adjunct'
import ExchangeService from 'src/services/ExchangeService'
import DictionariesService from 'src/services/DictionariesService'
import LaravelEchoService from 'src/services/LaravelEchoService'
import {
  ERROR_DELAY_TIME,
  EXCHANGE_TYPES,
  EXCHANGE_EVENT_TYPES
} from 'src/ducks/consts'
import { selectDictionaries } from 'src/ducks/selectors'
import { selectCurrentUserId } from 'src/features/account/duck/selectors'
import {
  selectGeneralActiveTab,
  selectGeneralFilters,
  selectGeneralFiltersFormatted,
  selectGeneralFiltersTabsList,
  selectGeneralTabsData
} from 'src/features/stocks/ducks/selectors'
import { setSnackbarValues } from 'src/ducks/actions'
import translate from 'src/intl/translate'
import {
  displayResponseErrorMessage,
  getErrorMessageFromApiResponse
} from 'src/utils/helpers'
import { normalizeToLocationsIds, normalizeToCompaniesIds } from './normalizers'

export const getGeneralOffersRoutine = createRoutine('GET_GENERAL_OFFERS')
export const clearGeneralOffersRoutine = createRoutine('CLEAR_GENERAL_OFFERS')
export const cleanGeneralOffersDetailsRoutine = createRoutine(
  'CLEAN_GENERAL_OFFERS_DETAILS'
)
export const showGeneralOfferDetailsRoutine = createRoutine(
  'SHOW_GENERAL_OFFER_DETAILS'
)
export const setGeneralDrawerRoutine = createRoutine('SET_GENERAL_DRAWER')
export const setGeneralFiltersRoutine = createRoutine('SET_GENERAL_FILTERS')
export const updateGeneralOfferRoutine = createRoutine('UPDATE_GENERAL_OFFER')
export const reserveGeneralOfferRoutine = createRoutine('RESERVE_GENERAL_OFFER')
export const bidGeneralOfferRoutine = createRoutine('BID_GENERAL_OFFER')
export const refreshGeneralOfferRoutine = createRoutine('REFRESH_GENERAL_OFFER')
export const makeGeneralOfferPublicRoutine = createRoutine(
  'MAKE_GENERAL_OFFER_PUBLIC'
)
export const clearGeneralFiltersRoutine = createRoutine('CLEAR_GENERAL_FILTERS')
export const getGeneralFilterTabsListRoutine = createRoutine(
  'GET_GENERAL_FILTER_TABS_LIST'
)
export const createGeneralFilterTabRoutine = createRoutine(
  'CREATE_GENERAL_FILTER_TAB'
)
export const removeGeneralFilterTabRoutine = createRoutine(
  'REMOVE_GENERAL_FILTER_TAB'
)
export const setGeneralActiveTabRoutine = createRoutine(
  'SET_GENERAL_ACTIVE_TAB'
)

export const GENERAL_TAB_EVENT_OFFER_CREATED = 'GENERAL_TAB_EVENT_OFFER_CREATED'
export const GENERAL_TAB_EVENT_OFFER_REFRESHED =
  'GENERAL_TAB_EVENT_OFFER_REFRESHED'
export const GENERAL_TAB_EVENT_OFFER_DELETED = 'GENERAL_TAB_EVENT_OFFER_DELETED'
export const GENERAL_OFFERS_LIST_UPDATED_BY_TAB_LIVE_DATA =
  'GENERAL_OFFERS_LIST_UPDATED_BY_TAB_LIVE_DATA'
export const generalOffersCleanerRoutine = createRoutine(
  'GENERAL_OFFERS_CLEANER'
)

//actions

function* getGeneralOffers() {
  yield put(getGeneralOffersRoutine.request())
  try {
    const activeTab = yield select(selectGeneralActiveTab)

    //case when initially loads
    if (isFalsy(activeTab)) {
      yield call(getGeneralFilterTabsList)
      const tabs = yield select(selectGeneralFiltersTabsList)
      yield call(setGeneralActiveTab, { payload: { id: head(tabs).id } })
      return
    }

    const tabs = yield select(selectGeneralFiltersTabsList)
    const filters = yield select(selectGeneralFiltersFormatted)

    const data = yield call(
      ExchangeService.getGeneralOffersListByTab,
      isFalsy(activeTab) ? head(tabs).id : activeTab,
      filters
    )

    yield put(getGeneralOffersRoutine.success(data))
    yield call(getGeneralFilterTabsList)
  } catch (e) {
    yield put(getGeneralOffersRoutine.failure())
    console.log(e)
  }
}

function* clearGeneralOffers() {
  yield put(clearGeneralOffersRoutine.success())
}

function* cleanGeneralOffersDetails() {
  yield put(cleanGeneralOffersDetailsRoutine.success())
}

function* showGeneralOfferDetails({ payload }) {
  yield put(showGeneralOfferDetailsRoutine.request())
  try {
    const { data } = yield call(ExchangeService.showGeneralOffer, payload)
    yield put(showGeneralOfferDetailsRoutine.success(data))
  } catch (e) {
    const message = getErrorMessageFromApiResponse(e)
    yield put(showGeneralOfferDetailsRoutine.failure({ message }))
    yield delay(ERROR_DELAY_TIME)
  } finally {
    yield put(showGeneralOfferDetailsRoutine.fulfill())
  }
}

function* setGeneralDrawer({ payload }) {
  yield put(setGeneralDrawerRoutine.success(payload))
}

function* setGeneralFilters({ payload }) {
  const dictionaries = yield select(selectDictionaries)

  yield put(setGeneralFiltersRoutine.success({ ...payload, dictionaries }))
  yield* getGeneralOffers()
}

function* clearGeneralFilters() {
  yield put(clearGeneralFiltersRoutine.success())
  yield* getGeneralOffers()
}

function* updateGeneralOffer({ payload }) {
  yield put(updateGeneralOfferRoutine.request())
  try {
    const { data } = yield call(
      ExchangeService.updateGeneralOffer,
      payload.id,
      payload.values
    )
    yield put(updateGeneralOfferRoutine.success(data))
  } catch (e) {
    const message = getErrorMessageFromApiResponse(e)
    yield put(updateGeneralOfferRoutine.failure({ message }))
    yield delay(ERROR_DELAY_TIME)
  } finally {
    yield put(updateGeneralOfferRoutine.fulfill())
  }
}

function* reserveGeneralOffer({ payload }) {
  yield put(reserveGeneralOfferRoutine.request())
  try {
    yield call(ExchangeService.reserveGeneralOffer, payload.id)
    yield put(reserveGeneralOfferRoutine.success())
    yield* showGeneralOfferDetails({ payload })
  } catch (e) {
    const message = getErrorMessageFromApiResponse(e)
    yield put(reserveGeneralOfferRoutine.failure({ message }))
    yield delay(ERROR_DELAY_TIME)
  } finally {
    yield put(reserveGeneralOfferRoutine.fulfill())
  }
}

function* bidGeneralOffer({ payload }) {
  yield put(bidGeneralOfferRoutine.request())
  try {
    yield call(ExchangeService.bidGeneralOffer, payload.id, {
      price: payload.price
    })
    yield put(bidGeneralOfferRoutine.success())
    yield call(showGeneralOfferDetails, { payload: { id: payload.id } })
    yield fork(setSnackbarValues, {
      payload: {
        message: translate().formatMessage({
          id: 'makeABid.successMessage'
        })
      }
    })
  } catch (e) {
    const message = getErrorMessageFromApiResponse(e)
    yield put(bidGeneralOfferRoutine.failure({ message }))
    yield call(displayResponseErrorMessage, e)
  } finally {
    yield delay(ERROR_DELAY_TIME)
    yield put(bidGeneralOfferRoutine.fulfill())
  }
}

function* refreshGeneralOffer({ payload }) {
  yield put(refreshGeneralOfferRoutine.request())
  try {
    yield call(ExchangeService.refreshGeneralOffer, payload.id)
    yield put(refreshGeneralOfferRoutine.success())
    yield* showGeneralOfferDetails({ payload })
    yield fork(setSnackbarValues, {
      payload: {
        message: translate().formatMessage({
          id: 'myOffers.refreshOfferSuccessMessage'
        })
      }
    })
  } catch (e) {
    const message = getErrorMessageFromApiResponse(e)
    yield put(refreshGeneralOfferRoutine.failure({ message }))
    yield call(displayResponseErrorMessage, e)
  } finally {
    yield delay(ERROR_DELAY_TIME)
    yield put(refreshGeneralOfferRoutine.fulfill())
  }
}

function* makeGeneralOfferPublic({ payload }) {
  yield put(makeGeneralOfferPublicRoutine.request())
  try {
    yield call(ExchangeService.makeGeneralOfferPublic, payload.id)
    yield put(makeGeneralOfferPublicRoutine.success())
    yield* showGeneralOfferDetails({ payload })
    yield fork(setSnackbarValues, {
      payload: {
        message: translate().formatMessage({
          id: 'myOffers.makeOfferPublicSuccessMessage'
        })
      }
    })
  } catch (e) {
    const message = getErrorMessageFromApiResponse(e)
    yield put(makeGeneralOfferPublicRoutine.failure({ message }))
    yield call(displayResponseErrorMessage, e)
  } finally {
    yield delay(ERROR_DELAY_TIME)
    yield put(makeGeneralOfferPublicRoutine.fulfill())
  }
}

function listenToExchangeTabEvents(data) {
  return eventChannel(emitter => {
    LaravelEchoService.getInstance().listenToExchangeTabs({
      exchangeType: data.exchangeType,
      userId: data.userId,
      tabsIds: data.tabsIds,
      callback: (tabId, event, payload) => {
        switch (event) {
          case EXCHANGE_EVENT_TYPES.OFFER_CREATED:
            return emitter({
              type: GENERAL_TAB_EVENT_OFFER_CREATED,
              tabId,
              payload
            })
          case EXCHANGE_EVENT_TYPES.OFFER_REFRESHED:
            return emitter({
              type: GENERAL_TAB_EVENT_OFFER_REFRESHED,
              tabId,
              payload
            })
          case EXCHANGE_EVENT_TYPES.OFFER_DELETED:
            return emitter({
              type: GENERAL_TAB_EVENT_OFFER_DELETED,
              tabId,
              payload
            })
          default:
            //handle information about not handled type
            return null
        }
      }
    })
    // unsubscribe function
    return () => {
      // do whatever to interrupt the socket communication here
    }
  })
}

export function* getGeneralFilterTabsList() {
  try {
    const tabs = yield call(ExchangeService.getGeneralFilterTabsList)
    const locationsIds = normalizeToLocationsIds(tabs.data)
    const companiesIds = normalizeToCompaniesIds(tabs.data)

    const locations = yield call(DictionariesService.locations, {
      filter: {
        id: locationsIds
      }
    })
    const companies = yield call(DictionariesService.companies, {
      filter: {
        id: companiesIds
      }
    })

    yield put(
      getGeneralFilterTabsListRoutine.success({
        data: tabs.data,
        locations: locations.data,
        companies: companies.data
      })
    )

    //listen to tabs events
    const userId = yield select(selectCurrentUserId)
    yield spawn(listenToExchangeTabEventsWatcher, {
      exchangeType: EXCHANGE_TYPES.GENERAL,
      userId,
      tabsIds: pluck('id')(tabs.data)
    })
  } catch (e) {
    console.log(e)
  }
}

function* createGeneralFilterTab({ payload }) {
  try {
    const filters = yield select(selectGeneralFilters)
    const activeTab = yield select(selectGeneralActiveTab)
    const tabs = yield select(selectGeneralFiltersTabsList)
    const activeTabData = head(tabs.filter(v => v.id === activeTab))

    const { data } = yield call(ExchangeService.createGeneralFilterTab, {
      name: payload.empty ? null : filters.tabName
    })
    yield put(
      createGeneralFilterTabRoutine.success({
        data,
        currentFilters: payload.empty ? null : activeTabData.currentFilters
      })
    )
    //set active new tab

    yield call(setGeneralActiveTab, { payload: { id: data.id } })
  } catch (e) {
    console.log(e)
  }
}

function* removeGeneralFilterTab({ payload }) {
  try {
    yield call(ExchangeService.removeGeneralFilterTab, payload.id)
    yield put(removeGeneralFilterTabRoutine.success({ id: payload.id }))
    //after removal set active tab to first one
    const tabs = yield select(selectGeneralFiltersTabsList)
    yield call(setGeneralActiveTab, { payload: { id: head(tabs).id } })
  } catch (e) {
    console.log(e)
  }
}

function* setGeneralActiveTab({ payload }) {
  try {
    const tabs = yield select(selectGeneralFiltersTabsList)
    const tab = head(tabs.filter(v => v.id === payload.id))
    const tabsData = yield select(selectGeneralTabsData)

    yield put(
      setGeneralActiveTabRoutine.success({
        activeTab: tab.id,
        filters: tab.currentFilters
      })
    )
    yield call(getGeneralOffers)

    //if tab has live data stored move them to offers list
    if (!isFalsy(tabsData[tab.id])) {
      yield put({
        type: GENERAL_OFFERS_LIST_UPDATED_BY_TAB_LIVE_DATA,
        payload: { tabId: tab.id, tabData: tabsData[tab.id] }
      })
    }
  } catch (e) {
    console.log(e)
  }
}

function* generalOffersCleaner() {
  yield put(generalOffersCleanerRoutine.success())
}

//watchers

function* getGeneralOffersRoutineWatcher() {
  yield takeLatest(getGeneralOffersRoutine.TRIGGER, getGeneralOffers)
}

function* clearGeneralOffersRoutineWatcher() {
  yield takeLatest(clearGeneralOffersRoutine.TRIGGER, clearGeneralOffers)
}

function* cleanGeneralOffersDetailsRoutineWatcher() {
  yield takeLatest(
    cleanGeneralOffersDetailsRoutine.TRIGGER,
    cleanGeneralOffersDetails
  )
}

function* showGeneralOfferDetailsRoutineWatcher() {
  yield takeLatest(
    showGeneralOfferDetailsRoutine.TRIGGER,
    showGeneralOfferDetails
  )
}

function* setGeneralDrawerRoutineWatcher() {
  yield takeLatest(setGeneralDrawerRoutine.TRIGGER, setGeneralDrawer)
}

function* setGeneralFiltersRoutineWatcher() {
  yield takeLatest(setGeneralFiltersRoutine.TRIGGER, setGeneralFilters)
}

function* updateGeneralOfferRoutineWatcher() {
  yield takeLatest(updateGeneralOfferRoutine.TRIGGER, updateGeneralOffer)
}

function* reserveGeneralOfferRoutineWatcher() {
  yield takeLatest(reserveGeneralOfferRoutine.TRIGGER, reserveGeneralOffer)
}

function* bidGeneralOfferRoutineWatcher() {
  yield takeLatest(bidGeneralOfferRoutine.TRIGGER, bidGeneralOffer)
}

function* refreshGeneralOfferRoutineWatcher() {
  yield takeLatest(refreshGeneralOfferRoutine.TRIGGER, refreshGeneralOffer)
}

function* makeGeneralOfferPublicRoutineWatcher() {
  yield takeLatest(
    makeGeneralOfferPublicRoutine.TRIGGER,
    makeGeneralOfferPublic
  )
}

function* clearGeneralFiltersRoutineWatcher() {
  yield takeLatest(clearGeneralFiltersRoutine.TRIGGER, clearGeneralFilters)
}

function* getGeneralFilterTabsListRoutineWatcher() {
  yield takeLatest(
    getGeneralFilterTabsListRoutine.TRIGGER,
    getGeneralFilterTabsList
  )
}

function* createGeneralFilterTabRoutineWatcher() {
  yield takeLatest(
    createGeneralFilterTabRoutine.TRIGGER,
    createGeneralFilterTab
  )
}

function* removeGeneralFilterTabRoutineWatcher() {
  yield takeLatest(
    removeGeneralFilterTabRoutine.TRIGGER,
    removeGeneralFilterTab
  )
}

function* setGeneralActiveTabRoutineWatcher() {
  yield takeLatest(setGeneralActiveTabRoutine.TRIGGER, setGeneralActiveTab)
}

function* listenToExchangeTabEventsWatcher(payload) {
  const channel = yield call(listenToExchangeTabEvents, payload)
  while (true) {
    const action = yield take(channel)
    yield put(action)
  }
}

function* generalOffersCleanerRoutineWatcher() {
  yield takeLatest(generalOffersCleanerRoutine.TRIGGER, generalOffersCleaner)
}

export const exchangeGeneralSagas = [
  fork(getGeneralOffersRoutineWatcher),
  fork(clearGeneralOffersRoutineWatcher),
  fork(cleanGeneralOffersDetailsRoutineWatcher),
  fork(showGeneralOfferDetailsRoutineWatcher),
  fork(setGeneralDrawerRoutineWatcher),
  fork(setGeneralFiltersRoutineWatcher),
  fork(updateGeneralOfferRoutineWatcher),
  fork(reserveGeneralOfferRoutineWatcher),
  fork(refreshGeneralOfferRoutineWatcher),
  fork(clearGeneralFiltersRoutineWatcher),
  fork(getGeneralFilterTabsListRoutineWatcher),
  fork(createGeneralFilterTabRoutineWatcher),
  fork(removeGeneralFilterTabRoutineWatcher),
  fork(setGeneralActiveTabRoutineWatcher),
  fork(generalOffersCleanerRoutineWatcher),
  fork(bidGeneralOfferRoutineWatcher),
  fork(makeGeneralOfferPublicRoutineWatcher)
]
